import React, { useState, useEffect, useRef } from 'react';
import api from '../../../services/api';
import CustomVideoPlayer from './CustomVideoPlayer';
import PDFViewer from './PDFViewer';

const ModuleDetailView = ({
  module,
  getModuleProgress,
  onBack,
  viewed,
  markViewed,
  unmarkViewed,
}) => {
  const moduleProgress = getModuleProgress(module.id);
  const [selectedLessonIndex, setSelectedLessonIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('video'); // 'video' or 'pdf'
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfFullscreen, setPdfFullscreen] = useState(false);
  const [pdfDesktopFullscreen, setPdfDesktopFullscreen] = useState(false);
  const loadedPdfUrlRef = useRef(null);

  const selectedLesson = module.lessons[selectedLessonIndex];

  // (Nota) função de extração de ID do YouTube removida — não utilizada aqui

  // Formata a duração de segundos para um formato legível.
  const formatDuration = seconds => {
    if (!seconds || seconds <= 0) return null;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    let durationString = '';
    if (minutes > 0) durationString += `${minutes} min `;
    if (remainingSeconds > 0) durationString += `${remainingSeconds} seg`;

    return durationString.trim();
  };

  // Determina o tipo de conteúdo da aula
  const getLessonContentType = lesson => {
    const hasVideo =
      lesson.content &&
      (lesson.content.includes('youtube.com') || lesson.content.includes('youtu.be'));
    const hasPDF = lesson.materials?.find(material => material.type === 'PDF')?.url;

    if (hasVideo && hasPDF) return 'mixed'; // Video + PDF
    if (hasVideo) return 'video';
    if (hasPDF) return 'pdf';
    return 'text'; // Apenas texto/descrição
  };

  const pdfMaterial = selectedLesson?.materials?.find(material => material.type === 'PDF');
  const hasPDF = pdfMaterial?.url;
  const hasVideo =
    selectedLesson?.content &&
    (selectedLesson.content.includes('youtube.com') || selectedLesson.content.includes('youtu.be'));
  // pdfMaterial.url será usado diretamente quando for necessário carregar

  // Determina qual tab mostrar por padrão
  useEffect(() => {
    if (hasVideo) {
      setActiveTab('video');
    } else if (hasPDF) {
      setActiveTab('pdf');
    }
  }, [selectedLessonIndex, hasVideo, hasPDF]);

  // Listener para remover elemento ytp-title-channel-logo
  useEffect(() => {
    const removeChannelLogo = () => {
      try {
        document.querySelectorAll('a.ytp-title-channel-logo').forEach(e => e.remove());
      } catch (e) {
        // Silenciosamente ignorar erros
      }
    };

    // Rodar imediatamente
    removeChannelLogo();

    // Monitorar continuamente
    const interval = setInterval(removeChannelLogo, 800);

    return () => clearInterval(interval);
  }, [activeTab]);

  // Carrega PDF como blob quando activeTab muda para 'pdf'
  useEffect(() => {
    if (activeTab !== 'pdf' || !hasPDF) {
      return () => {};
    }

    const pdfUrl = pdfMaterial?.url;
    if (!pdfUrl || loadedPdfUrlRef.current === pdfUrl) {
      return () => {};
    }

    const loadPdf = async () => {
      setPdfLoading(true);
      try {
        // Extrair nome do arquivo da URL
        let filename = '';
        if (pdfUrl.startsWith('/uploads/')) {
          filename = pdfUrl.replace('/uploads/', '');
        } else if (pdfUrl.includes('/')) {
          filename = pdfUrl.split('/').pop();
        } else {
          filename = pdfUrl;
        }

        // Fazer download via API com autenticação
        const response = await api.getWithoutBaseAPI(`/uploads/${filename}`, {
          responseType: 'blob',
        });

        // Guardar Blob para o leitor custom (não depender de iframe do navegador)
        const blob = new Blob([response.data], { type: 'application/pdf' });
        setPdfBlob(blob);
        loadedPdfUrlRef.current = pdfUrl;
      } catch (err) {
        console.error('Erro ao carregar PDF:', err);
      } finally {
        setPdfLoading(false);
      }
    };

    loadPdf();

    // Cleanup: nada especial (usamos Blob em memória)
    return () => {};
  }, [activeTab, hasPDF, pdfMaterial?.url]);

  // Handle ESC key to close PDF fullscreen
  useEffect(() => {
    const handleKeyPress = e => {
      if (e.key === 'Escape') {
        if (pdfFullscreen) {
          setPdfFullscreen(false);
        }
        if (pdfDesktopFullscreen) {
          setPdfDesktopFullscreen(false);
        }
      }
    };

    if (pdfFullscreen || pdfDesktopFullscreen) {
      document.addEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'auto';
    };
  }, [pdfFullscreen, pdfDesktopFullscreen]);

  // Limpar blob quando sair da tab PDF (libera memória e evita mostrar conteúdo antigo)
  useEffect(() => {
    if (activeTab !== 'pdf') {
      setPdfBlob(null);
      loadedPdfUrlRef.current = null;
    }
  }, [activeTab]);

  return (
    <div className="module-detail-layout">
      {/* Header */}
      <div className="module-detail-header">
        <div className="module-detail-info">
          <h1 className="module-detail-title">{module.title}</h1>
          <p className="module-detail-subtitle">
            {module.lessons.length === 1 ? '1 aula' : `${module.lessons.length} aulas`}
          </p>
        </div>

        <div className="module-detail-controls">
          <button className="back-btn detail-back-btn" onClick={onBack}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="15,18 9,12 15,6" />
            </svg>
            Voltar
          </button>

          <div className="module-detail-progress">
            <svg width="60" height="60" viewBox="0 0 40 40" className="progress-circle-svg-detail">
              <circle cx="20" cy="20" r="17" fill="none" stroke="#e5e7eb" strokeWidth="4" />
              <circle
                cx="20"
                cy="20"
                r="17"
                fill="none"
                stroke={moduleProgress === 100 ? '#10b981' : '#3b82f6'}
                strokeWidth="4"
                strokeLinecap="round"
                transform="rotate(-90 20 20)"
                style={{
                  strokeDasharray: 106.8,
                  strokeDashoffset: 106.8 - (moduleProgress / 100) * 106.8,
                  transition: 'stroke-dashoffset 0.5s ease-out, stroke 0.5s ease',
                }}
              />
            </svg>
            <span className={`progress-text ${moduleProgress === 100 ? 'completed' : ''}`}>
              {moduleProgress}%
            </span>
          </div>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="module-content-layout">
        {/* Left: Main Content */}
        <div className="module-main-content">
          {selectedLesson && (
            <>
              {/* Content Tabs */}
              {(hasVideo || hasPDF) && (
                <div className="lesson-content-tabs">
                  {hasVideo && (
                    <button
                      onClick={() => setActiveTab('video')}
                      className={`tab-button ${activeTab === 'video' ? 'active' : ''}`}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polygon points="5,3 19,12 5,21" />
                      </svg>
                      Vídeo
                    </button>
                  )}
                  {hasPDF && (
                    <button
                      onClick={() => setActiveTab('pdf')}
                      className={`tab-button ${activeTab === 'pdf' ? 'active' : ''}`}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14,2 14,8 20,8" />
                      </svg>
                      Material PDF
                    </button>
                  )}
                </div>
              )}

              {/* Tab Content - Video */}
              {activeTab === 'video' && hasVideo && (
                <div className="lesson-content-player">
                  {/* Player customizado com YouTube API */}
                  <CustomVideoPlayer
                    videoUrl={selectedLesson.content}
                    title={selectedLesson.title}
                    onVideoComplete={() => markViewed(selectedLesson.id)}
                  />
                </div>
              )}

              {/* Tab Content - PDF */}
              {activeTab === 'pdf' && hasPDF && (
                <>
                  <div className="lesson-content-player pdf-content-view">
                    <div className="pdf-player-wrapper">
                      {pdfLoading && (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            color: 'white',
                          }}
                        >
                          <p>Carregando PDF...</p>
                        </div>
                      )}
                      {pdfBlob && !pdfLoading && (
                        <>
                          {/* Desktop: iframe nativo do navegador */}
                          <div className="pdf-viewer-desktop">
                            <div className="pdf-desktop-header">
                              <button
                                className="pdf-fullscreen-btn"
                                onClick={() => setPdfDesktopFullscreen(true)}
                                title="Tela cheia"
                              >
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
                                </svg>
                              </button>
                            </div>
                            <iframe
                              src={URL.createObjectURL(pdfBlob)}
                              type="application/pdf"
                              className="pdf-iframe-native"
                              title={pdfMaterial?.name || 'material.pdf'}
                            />
                          </div>

                          {/* Mobile: PDFViewer customizado */}
                          <div className="pdf-viewer-mobile">
                            <div className="pdf-viewer-container">
                              <div className="pdf-blur-overlay pdf-blur-mobile-only">
                                <button
                                  className="pdf-open-btn"
                                  onClick={() => setPdfFullscreen(true)}
                                >
                                  Abrir
                                </button>
                              </div>
                              <PDFViewer
                                blob={pdfBlob}
                                fileName={pdfMaterial?.name || 'material.pdf'}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* PDF Fullscreen Modal - Desktop */}
                  {pdfDesktopFullscreen && pdfBlob && (
                    <div className="pdf-modal-fullscreen-overlay active">
                      <div className="pdf-modal-fullscreen-toolbar">
                        <button
                          className="pdf-modal-close-btn"
                          onClick={() => setPdfDesktopFullscreen(false)}
                          title="Fechar (ESC)"
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                          Fechar
                        </button>
                      </div>
                      <div className="pdf-modal-fullscreen-content">
                        <iframe
                          src={URL.createObjectURL(pdfBlob)}
                          type="application/pdf"
                          className="pdf-iframe-fullscreen"
                          title={pdfMaterial?.name || 'material.pdf'}
                        />
                      </div>
                    </div>
                  )}

                  {/* PDF Fullscreen Modal - Mobile */}
                  {pdfFullscreen && pdfBlob && (
                    <div className="pdf-modal-fullscreen-overlay active">
                      <div className="pdf-modal-fullscreen-toolbar">
                        <button
                          className="pdf-modal-close-btn"
                          onClick={() => setPdfFullscreen(false)}
                          title="Fechar (ESC)"
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                          Fechar
                        </button>
                      </div>
                      <div className="pdf-modal-fullscreen-content">
                        <PDFViewer blob={pdfBlob} fileName={pdfMaterial?.name || 'material.pdf'} />
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* No Content */}
              {!hasVideo && !hasPDF && (
                <div className="lesson-content-player">
                  <div className="content-placeholder">
                    <svg
                      width="80"
                      height="80"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1"
                    >
                      <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" />
                      <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
                    </svg>
                    <p>Sem conteúdo disponível</p>
                  </div>
                </div>
              )}

              {/* Lesson Info - BELOW tabs/content */}
              <div className="lesson-info-section">
                {/* Lesson Header */}
                <div className="lesson-main-header">
                  <h2 className="lesson-main-title">{selectedLesson.title}</h2>
                </div>

                {/* Lesson Description Section */}
                {selectedLesson.description && (
                  <div className="lesson-description-section">
                    <h3 className="description-label">Sobre esta aula</h3>
                    <p className="lesson-description-content" style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedLesson.description}
                    </p>
                  </div>
                )}

                {/* Lesson Meta Info */}
                {selectedLesson.durationSeconds > 0 && (
                  <div className="lesson-meta-section">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    <span>{formatDuration(selectedLesson.durationSeconds)}</span>
                  </div>
                )}

                {/* Complete Button */}
                <div className="lesson-completion-section">
                  <button
                    onClick={() =>
                      viewed(selectedLesson.id)
                        ? unmarkViewed(selectedLesson.id)
                        : markViewed(selectedLesson.id)
                    }
                    className={`btn-mark-complete ${viewed(selectedLesson.id) ? 'completed' : ''}`}
                  >
                    {viewed(selectedLesson.id) ? (
                      <>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <polyline points="20,6 9,17 4,12" />
                        </svg>
                        Concluído
                      </>
                    ) : (
                      <>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 8v4l3 2" />
                        </svg>
                        Marcar como concluído
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Right: Sidebar with Lessons */}
        <aside className="module-sidebar">
          <div className="sidebar-header">
            <h3 className="sidebar-title">Aulas</h3>
            <span className="sidebar-count">{module.lessons.length}</span>
          </div>

          <div className="lessons-list">
            {module.lessons.map((lesson, index) => {
              const contentType = getLessonContentType(lesson);
              const hasVideo =
                lesson.content &&
                (lesson.content.includes('youtube.com') || lesson.content.includes('youtu.be'));

              // Extrai ID do YouTube para gerar thumbnail
              const getYouTubeThumbnail = url => {
                if (!url) return null;
                const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
                const match = url.match(regExp);
                const videoId = match && match[2].length === 11 ? match[2] : null;
                return videoId ? `https://img.youtube.com/vi/${videoId}/default.jpg` : null;
              };

              const thumbnailUrl = hasVideo ? getYouTubeThumbnail(lesson.content) : null;

              return (
                <button
                  key={lesson.id}
                  onClick={() => setSelectedLessonIndex(index)}
                  className={`lesson-sidebar-item ${selectedLessonIndex === index ? 'active' : ''} ${viewed(lesson.id) ? 'completed' : ''}`}
                >
                  <div className="lesson-item-media">
                    {(contentType === 'video' || contentType === 'mixed') && thumbnailUrl ? (
                      <img
                        src={thumbnailUrl}
                        alt={lesson.title}
                        className="lesson-thumbnail-preview"
                      />
                    ) : (
                      <div className="lesson-item-icon">
                        {contentType === 'pdf' || contentType === 'text' ? (
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
                            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
                          </svg>
                        ) : null}
                      </div>
                    )}
                  </div>

                  <div className="lesson-item-number">
                    {viewed(lesson.id) ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="20,6 9,17 4,12" />
                      </svg>
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>

                  <div className="lesson-item-content">
                    <p className="lesson-item-title">{lesson.title}</p>
                    {lesson.durationSeconds > 0 && (
                      <span className="lesson-item-duration">
                        {formatDuration(lesson.durationSeconds)}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default ModuleDetailView;
